import { useSyncConfigV1QueryUpdaters } from '../query-updaters/use-query-updaters';
import { CreateTrainByPackageIO } from '../types';
import { UseSyncConfigV1MutationEndpointArgs, useSyncConfigV1Mutation } from './use-sync-config-v1-mutation';

type MutationContext<C = unknown> = {
  // Add internal mutation context here if needed (useful for optimistic updates)
  otherContext?: C;
};

/**
 * A hook that returns a mutation for the `CreateTrainByPackage` mutation endpoint.
 * It handles query invalidation for the relevant query endpoints internally.
 * @param options (optional) The options to pass to `useMutation`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param optimisticUpdate (optional) Whether to perform an optimistic update. Defaults to `false`.
 */
export const useCreateTrainByPackageMutation = <
  E = unknown,
  C = unknown,
  OtherOptions extends object = never,
  RequestOverride extends CreateTrainByPackageIO['input'] = CreateTrainByPackageIO['input']
>({
  options,
  optimisticUpdate: _optimisticUpdate = false,
  ...args
}: UseSyncConfigV1MutationEndpointArgs<
  'CreateTrainByPackage',
  E,
  C | undefined,
  OtherOptions,
  RequestOverride
> = {}) => {
  const { invalidateQueries } = useSyncConfigV1QueryUpdaters();

  return useSyncConfigV1Mutation<'CreateTrainByPackage', E, MutationContext<C>, OtherOptions, RequestOverride>({
    endpointName: 'CreateTrainByPackage',
    ...args,
    options: {
      ...options,
      onMutate: async (request) => {
        // Implement optimistic updates here

        return {
          // Add custom context type here (if exists in `MutationContext`)
          otherContext: await options?.onMutate?.(request),
        };
      },
      onSuccess: (response, request, context) => {
        invalidateQueries({ endpointName: 'ListTrains' });

        return options?.onSuccess?.(response, request, context?.otherContext);
      },
      onError: (error, request, context) => {
        // Revert optimistic update here (if implemented)

        return options?.onError?.(error, request, context?.otherContext);
      },
      onSettled: (response, error, request, context) => {
        // Only pass context of type C into provided `onSettled` option
        return options?.onSettled?.(response, error, request, context?.otherContext);
      },
    },
  });
};
